import React, { useState, useEffect } from 'react';
import { Plus, X, Pencil, Trash2 } from 'lucide-react';
import { updateMaterial, addMaterial, deleteMaterial, uploadTexture } from '../../firebase/materials';
import { AdvancedColorPicker } from './AdvancedColorPicker';
import { DeleteConfirmationModal } from './Modals';

const PropertyInput = React.memo(({ label, ...props }) => (
    <div>
        <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">{label}</label>
        <input
            {...props}
            className="w-full mt-1 px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 disabled:opacity-50"
        />
    </div>
));

export const MaterialEditor = ({ isOpen, onClose, ownerId, materialToEdit, onSave }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [price, setPrice] = useState('');
    const [currency, setCurrency] = useState('USD');
    const [linkUrl, setLinkUrl] = useState('');
    const [color, setColor] = useState('#ffffff');
    const [pattern, setPattern] = useState('none');
    const [textureUrl, setTextureUrl] = useState('');
    const [textureScale, setTextureScale] = useState(100);
    const [textureUploadMethod, setTextureUploadMethod] = useState('file');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [error, setError] = useState('');

    const patterns = ['none', 'running', 'herringbone', 'diamond', 'straight'];

    useEffect(() => {
        if (isOpen) {
            if (materialToEdit) {
                const initialTextureUrl = materialToEdit.textureUrl ?? '';
                setName(materialToEdit.name ?? '');
                setDescription(materialToEdit.description ?? '');
                setPrice(materialToEdit.price != null ? String(materialToEdit.price) : '');
                setCurrency(materialToEdit.currency ?? 'USD');
                setLinkUrl(materialToEdit.linkUrl || '');
                setColor(materialToEdit.color ?? '#ffffff');
                setPattern(materialToEdit.pattern ?? 'none');
                setTextureUrl(initialTextureUrl);
                setTextureScale(materialToEdit.textureScale ?? 100);

                if (initialTextureUrl && !initialTextureUrl.startsWith('data:') && !initialTextureUrl.startsWith('blob:')) {
                    setTextureUploadMethod('url');
                } else {
                    setTextureUploadMethod('file');
                }
            } else {
                setName('');
                setDescription('');
                setPrice('');
                setCurrency('USD');
                setLinkUrl('');
                setColor('#ffffff');
                setPattern('none');
                setTextureUrl('');
                setTextureScale(100);
                setTextureUploadMethod('file');
            }
            setUploadProgress(0);
            setError('');
            setIsSubmitting(false);
        }
    }, [materialToEdit, isOpen]);

    const handleTextureUpload = async (e) => {
        const file = e.target.files[0];
        if (file) {
            try {
                const url = await uploadTexture(file, (progress) => setUploadProgress(progress));
                setTextureUrl(url);
                setUploadProgress(100);
            } catch (err) {
                setError('Texture upload failed.');
                console.error(err);
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');
        
        const materialData = {
            name,
            description,
            price: parseFloat(price) || 0,
            currency,
            linkUrl,
            color,
            pattern,
            textureUrl: textureUrl || null,
            textureScale,
        };

        try {
            await onSave(materialData);
            onClose();
        } catch (err) {
            setError('Failed to save material.');
            console.error(err);
        } finally {
            setIsSubmitting(false);
        }
    };
    
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-[80] p-4">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl w-full max-w-md relative max-h-[90vh] overflow-y-auto">
                <button onClick={onClose} className="absolute top-3 right-3 p-1 rounded-full text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                    <X className="h-5 w-5" />
                </button>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">{materialToEdit ? 'Edit Material' : 'Create New Material'}</h2>
                    
                    <PropertyInput label="Material Name" value={name || ''} onChange={(e) => setName(e.target.value)} required />
                    
                    <div>
                        <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Base Color</label>
                        <AdvancedColorPicker
                            value={color}
                            onChange={setColor}
                        />
                    </div>

                    <div>
                        <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Texture</label>
                         <div className="flex items-center space-x-4 my-2">
                            <label className="flex items-center cursor-pointer">
                                <input type="radio" name="textureUploadMethod" value="file" checked={textureUploadMethod === 'file'} onChange={() => setTextureUploadMethod('file')} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" />
                                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Upload File</span>
                            </label>
                            <label className="flex items-center cursor-pointer">
                                <input type="radio" name="textureUploadMethod" value="url" checked={textureUploadMethod === 'url'} onChange={() => setTextureUploadMethod('url')} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" />
                                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">From URL</span>
                            </label>
                        </div>
                        {textureUploadMethod === 'file' ? (
                            <input type="file" accept="image/*" onChange={handleTextureUpload} className="w-full mt-1 text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:text-gray-400 dark:file:bg-gray-700 dark:file:text-gray-300 dark:hover:file:bg-gray-600 cursor-pointer" />
                        ) : (
                             <input type="url" value={textureUrl || ''} onChange={(e) => setTextureUrl(e.target.value)} placeholder="https://example.com/texture.png" className="w-full mt-1 px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600" />
                        )}
                        {uploadProgress > 0 && uploadProgress < 100 && (
                            <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2 dark:bg-gray-700">
                                <div className="bg-blue-600 h-1.5 rounded-full" style={{width: `${uploadProgress}%`}}></div>
                            </div>
                        )}
                        {textureUrl && (
                            <div className="mt-2 space-y-4">
                                <img src={textureUrl} alt="Texture preview" className="rounded-md max-h-20" />
                                <div>
                                    <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Texture Scale</label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="range"
                                            min="10"
                                            max="200"
                                            value={textureScale}
                                            onChange={(e) => setTextureScale(Number(e.target.value))}
                                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-600 accent-blue-600"
                                        />
                                        <span className="text-sm font-mono text-gray-500 dark:text-gray-400 w-10 text-right">{textureScale}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Vector Pattern</label>
                         <select value={pattern} onChange={(e) => setPattern(e.target.value)} className="w-full mt-1 px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 capitalize">
                            {patterns.map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                    </div>

                     <div className="border-t dark:border-gray-600 pt-4 space-y-4">
                        <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400">Details (Optional)</h3>
                        <div>
                             <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Description</label>
                             <textarea value={description || ''} onChange={(e) => setDescription(e.target.value)} rows="2" placeholder="e.g. 'Natural finish, wide plank'" className="w-full mt-1 px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600" />
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                            <div className="col-span-2">
                                <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Price</label>
                                <input type="number" value={price || ''} onChange={(e) => setPrice(e.target.value)} placeholder="e.g. 8.99" className="w-full mt-1 px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600" />
                            </div>
                            <div>
                                <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Currency</label>
                                <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="w-full mt-1 px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600">
                                    <option>USD</option>
                                    <option>EUR</option>
                                </select>
                            </div>
                        </div>
                        <div>
                            <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Product Link</label>
                            <input type="url" value={linkUrl || ''} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://example.com/product" className="w-full mt-1 px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600" />
                        </div>
                    </div>

                    {error && <p className="text-red-500 text-sm text-center">{error}</p>}

                    <button type="submit" disabled={isSubmitting} className={`w-full px-4 py-2 text-white font-medium rounded-lg ${isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}>
                        {isSubmitting ? 'Saving...' : 'Save Material'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export const MaterialLibrary = ({ onSelectItem, ownerId, materials, canManage, onEditItem, purpose }) => {
    const [materialToDelete, setMaterialToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleDeleteRequest = (e, material) => {
        e.stopPropagation();
        setMaterialToDelete(material);
    };

    const handleConfirmDelete = async () => {
        if (!materialToDelete) return;
        setIsDeleting(true);
        try {
            await deleteMaterial(materialToDelete.id, materialToDelete.textureUrl);
        } catch (error) {
            console.error("Failed to delete material", error);
        } finally {
            setIsDeleting(false);
            setMaterialToDelete(null);
        }
    };

    const handleClick = (e, item) => {
        e.stopPropagation();
        if (purpose === 'add' || purpose === 'assign') {
            onSelectItem(item);
            return;
        }

        if (canManage && item.ownerId === ownerId) {
            onEditItem(item);
        } else {
            onSelectItem(item);
        }
    }

    return (
        <>
            <div className="flex justify-end mb-4 flex-shrink-0">
                {canManage && <button onClick={() => onEditItem(null)} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                    <Plus className="h-4 w-4" />
                    Add New
                </button>}
            </div>

            <div className="flex-grow overflow-y-auto pr-2 -mr-2">
                {materials.length > 0 ? (
                     <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                        {materials.map(material => (
                            <div key={material.id} onClick={(e) => handleClick(e, material)} className="group relative border dark:border-gray-700 rounded-lg cursor-pointer overflow-hidden aspect-square flex flex-col hover:shadow-lg hover:border-blue-500 dark:hover:border-blue-500 transition-all">
                                <div className="w-full h-full bg-cover bg-center" style={{backgroundColor: material.color, backgroundImage: `url(${material.textureUrl})`}}>
                                </div>
                                <div className="p-2 bg-white/70 dark:bg-black/70 backdrop-blur-sm w-full">
                                    <p className="text-sm font-semibold truncate text-gray-800 dark:text-gray-200">{material.name}</p>
                                </div>
                                {canManage && material.ownerId === ownerId && (
                                    <div className="absolute top-1 right-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); onEditItem(material); }} 
                                            className="p-1.5 bg-white/50 dark:bg-black/50 backdrop-blur-sm rounded-full text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400"
                                            aria-label={`Edit ${material.name}`}
                                        >
                                            <Pencil className="h-4 w-4" />
                                        </button>
                                        <button 
                                            onClick={(e) => handleDeleteRequest(e, material)} 
                                            className="p-1.5 bg-white/50 dark:bg-black/50 backdrop-blur-sm rounded-full text-gray-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400" 
                                            aria-label={`Delete ${material.name}`}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400">
                        <X className="h-12 w-12" />
                        <p className="mt-4 text-lg">Your library is empty.</p>
                        {canManage && <p className="text-sm">Click "Add New" to create your first material.</p>}
                    </div>
                )}
            </div>
            
            <DeleteConfirmationModal
                isOpen={!!materialToDelete}
                onClose={() => setMaterialToDelete(null)}
                onConfirm={handleConfirmDelete}
                item={materialToDelete}
                isDeleting={isDeleting}
            />
        </>
    );
};
